import React from 'react';
import { Input } from 'reactstrap';
import { toast } from 'react-toastify';
import EmployeeService from '../../../services/Employee';
import PerformanceVerbalWarningService from '../../../services/PerformanceVerbalWarning';
import PolicyViolationsService from '../../../services/PolicyViolations';
import { FilteredOptionsInterface } from '../../../entities/FilteredOptions';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';

export interface VerbalWarningImportData {
  employeesList: any[];
  policyViolationsList: any[];
}

export const loadVerbalWarningData = async (token: string, schoolId: string): Promise<VerbalWarningImportData> => {
  const employeesList = await loadEmployees(token, schoolId);
  const policyViolationsList = await loadPolicyViolations(token);

  return {
    employeesList,
    policyViolationsList,
  };
};

const loadEmployees = async (token: string, schoolId: string): Promise<any[]> => {
  try {
    const filteredOptions: FilteredOptionsInterface = {
      filters: [],
      options: createOptionsPage(createPagedMeta()),
    };
    filteredOptions.options.itemsPerPage = 100000;
    const result = await EmployeeService(token).listEmployeesBySchool(schoolId, '', filteredOptions);
    const employeesWithUsers = (result.data || []).filter((emp: any) => emp.user);
    return employeesWithUsers || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No employees found');
    return [];
  }
};

const loadPolicyViolations = async (token: string): Promise<any[]> => {
  try {
    const result = await PolicyViolationsService(token).list();
    return result.data || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No policy violations found');
    return [];
  }
};

export interface ProcessedVerbalWarning {
  userId: string;
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForVerbalWarning: string;
  nextConsequences: string;
  expectationOfImprovement: string;
  privateNotes: string;
  approved: boolean;
}

export interface ProcessingErrors {
  [key: string]: string;
}

export const processVerbalWarningImport = (
  importedLines: any[],
  formData: any[],
  employeesList: any[],
  policyViolationsList: any[],
): { verbalWarnings: ProcessedVerbalWarning[]; errors: ProcessingErrors } => {
  const verbalWarnings: ProcessedVerbalWarning[] = [];
  const errors: ProcessingErrors = {};

  for (let i = 0; i < importedLines.length; i++) {
    const line = importedLines[i];
    const formLine = formData[i] || {};

    const firstName = formLine['first_name'] || line['first_name'] || '';
    const lastName = formLine['last_name'] || line['last_name'] || '';
    const staffNameOrId =
      formLine['staff_name'] ||
      formLine['staffname'] ||
      line['staff_name'] ||
      line['staffname'] ||
      formLine['staff'] ||
      line['staff'] ||
      `${firstName} ${lastName}`.trim() ||
      '';
    const dateOfIncident =
      formLine['date_of_incident'] ||
      formLine['dateofincident'] ||
      line['date_of_incident'] ||
      line['dateofincident'] ||
      formLine['date'] ||
      line['date'] ||
      '';
    const policyViolationsStr =
      formLine['policy_violations'] ||
      formLine['policyviolations'] ||
      line['policy_violations'] ||
      line['policyviolations'] ||
      formLine['policy_violation'] ||
      line['policy_violation'] ||
      '';
    const reasonForVerbal =
      formLine['reason_for_verbal'] ||
      formLine['reasonforverbal'] ||
      line['reason_for_verbal'] ||
      line['reasonforverbal'] ||
      formLine['reason'] ||
      line['reason'] ||
      '';
    const nextConsequences =
      formLine['next_consequences'] ||
      formLine['nextconsequences'] ||
      line['next_consequences'] ||
      line['nextconsequences'] ||
      formLine['training'] ||
      line['training'] ||
      '';
    const expectationOfImprovement =
      formLine['expectations_of_improvement'] ||
      formLine['expectationsofimprovement'] ||
      formLine['expectations_for_improvement'] ||
      formLine['expectationsforimprovement'] ||
      line['expectations_of_improvement'] ||
      line['expectationsofimprovement'] ||
      line['expectations_for_improvement'] ||
      line['expectationsforimprovement'] ||
      formLine['goal'] ||
      line['goal'] ||
      '';
    const givenBy = formLine['given_by'] || line['given_by'] || '';
    const center = formLine['center'] || line['center'] || '';
    const privateNotes =
      formLine['private_notes'] ||
      formLine['privatenotes'] ||
      line['private_notes'] ||
      line['privatenotes'] ||
      [givenBy ? `Given by: ${String(givenBy).trim()}` : '', center ? `Center: ${String(center).trim()}` : '']
        .filter((v) => v.length > 0)
        .join('\n');

    let employee = employeesList.find((emp: any) => emp.user?.id === staffNameOrId);
    if (!employee) {
      employee = employeesList.find((emp: any) => {
        const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
        return fullName.toLowerCase() === String(staffNameOrId).toLowerCase().trim();
      });
    }

    if (!employee || !employee.user) {
      errors[`${i}.userId`] = `Staff member "${staffNameOrId}" not found`;
      continue;
    }

    const cleanedPolicyViolationsStr = String(policyViolationsStr || '').trim();
    const lowerPolicyViolations = cleanedPolicyViolationsStr.toLowerCase();
    const isNonePolicyViolation =
      cleanedPolicyViolationsStr.length === 0 ||
      lowerPolicyViolations === 'none' ||
      lowerPolicyViolations.startsWith('none ') ||
      lowerPolicyViolations.startsWith('none |');

    const policyViolationNames = isNonePolicyViolation
      ? []
      : cleanedPolicyViolationsStr
          .split(/[,;\r\n]+/)
          .map((name: string) => name.trim())
          .filter((name: string) => name.length > 0);

    const policyViolationIds: string[] = [];
    const notFoundViolations: string[] = [];

    for (const pvName of policyViolationNames) {
      const pv = policyViolationsList.find((pv: any) => pv.name.toLowerCase() === pvName.toLowerCase());
      if (pv) {
        policyViolationIds.push(pv.id);
      } else {
        notFoundViolations.push(pvName);
      }
    }

    if (notFoundViolations.length > 0) {
      errors[`${i}.policyViolations`] = `Policy violation${
        notFoundViolations.length > 1 ? 's' : ''
      } not found: ${notFoundViolations.join(', ')}`;
    }

    if (policyViolationIds.length === 0 && policyViolationNames.length === 0) {
      errors[`${i}.policyViolations`] = 'At least one policy violation is required';
    } else if (policyViolationIds.length === 0 && policyViolationNames.length > 0) {
      if (!errors[`${i}.policyViolations`]) {
        errors[`${i}.policyViolations`] = 'No valid policy violations found';
      }
    }

    if (!dateOfIncident) {
      errors[`${i}.date`] = 'Date of Incident is required';
    }

    if (!reasonForVerbal.trim()) {
      errors[`${i}.reasonForVerbalWarning`] = 'Reason For Verbal is required';
    }

    if (!expectationOfImprovement.trim()) {
      errors[`${i}.expectationOfImprovement`] = 'Expectations of Improvement / Goal is required';
    }

    if (Object.keys(errors).some((key) => key.startsWith(`${i}.`))) {
      continue;
    }

    verbalWarnings.push({
      userId: employee.user.id,
      name: `${employee.user.firstName} ${employee.user.lastName}`,
      date: dateOfIncident,
      position: employee.title?.name || '',
      department: employee.department?.name || '',
      supervisor: employee.supervisor?.name || '',
      dateOfHire: employee.startDate || '2024-01-01',
      policyViolationIds,
      reasonForVerbalWarning: reasonForVerbal,
      nextConsequences: nextConsequences || 'N/A',
      expectationOfImprovement,
      privateNotes,
      approved: true,
    });
  }

  return { verbalWarnings, errors };
};

export const submitVerbalWarningImport = async (
  token: string,
  schoolId: string,
  verbalWarnings: ProcessedVerbalWarning[],
): Promise<void> => {
  const service = PerformanceVerbalWarningService(token);
  await service.create({ schoolId, verbalWarnings, isImport: true });
};

export const getVerbalWarningFieldConfig = (
  value: string,
  name: string,
  employeesList: any[],
  _policyViolationsList: any[],
): JSX.Element | null => {
  if (
    name.split('][')[1].toLocaleLowerCase() === 'staff_name]' ||
    name.split('][')[1].toLocaleLowerCase() === 'staffname]'
  ) {
    const selected = employeesList.find((emp: any) => {
      const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
      return fullName.toLowerCase() === String(value).toLowerCase().trim() || emp.user?.id === value;
    });
    const defaultValue = selected?.user?.id || value || '';
    return (
      <Input type="select" defaultValue={defaultValue} name={name}>
        <option value={''}></option>
        {employeesList.map((emp: any) => {
          const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
          return (
            <option key={emp.user?.id} value={emp.user?.id}>
              {fullName}
            </option>
          );
        })}
      </Input>
    );
  }

  if (
    name.split('][')[1].toLocaleLowerCase() === 'policy_violations]' ||
    name.split('][')[1].toLocaleLowerCase() === 'policyviolations]' ||
    name.split('][')[1].toLocaleLowerCase() === 'policy_violation]'
  ) {
    return (
      <Input
        type="text"
        defaultValue={value}
        name={name}
        placeholder="Separate multiple with comma, semicolon or newline"
      />
    );
  }

  return null;
};
